// Companion/blocks/useQuestionStudyBadges.ts
//
// The badges under a missed question in the thread, and what studying them does
// (ORA-546/547, ORA-831).
//
// Two surfaces serve the same question body: a single served question, and a
// question inside a study set. Both may offer the two badges QBank offers after
// a miss — the QBank variant the miss scheduled, and the flashcards it assigned
// — so the wiring that carries the ids lives here rather than in either one.
//
// What differs is how the content is asked for, because the two are reached by
// different routes. A served question names its own block, and the add-block
// route rebuilds the content from the student's scheduling rows. A question
// inside a set has no block of its own — the set is the block — so the
// study-set route mints a review set from the set plus the question instead
// (ORA-830).

import { useRef } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { addReferenceBlock } from '@/slices/chat/addReferenceBlock';
import { addReviewStudySetBlock } from '@/slices/chat/addReviewStudySetBlock';
import type { ChatRootState } from '@/slices/chat/chatSelectors';
import type { ChatBlock } from '@/slices/chat/chatSlice';
import type { ChatAddBlockReference } from '@/services/chatConversations';
import type { StudySetReviewRefusal } from '@/services/studySets';
import { examsSelectors } from '@/slices/exams/examsSlice';
import { isPreclinicalExam } from '@/utils/examUtils';
import type { AppDispatch, RootState } from '@/store/store';
import type { QuestionBadgeAddResult } from './bodies/QuestionBlockBody/questionBadgeAddResult';
import {
  findAddedVariantBlockId,
  useScrollToAddedBlock,
} from './addedBlockScroll';

/**
 * Why a served question's badges cannot add anything yet. Their block is hung
 * off the answer that served it, and the server only names that answer when the
 * turn finishes — but a served question is answerable the moment it arrives, so
 * a student who answers mid-turn can reach the badges before the id exists.
 *
 * A question inside a set never waits: its review set is named by the set and
 * the question, both of which are in hand as soon as the set renders.
 */
const ANSWER_STILL_WRITING = 'Available when the answer finishes';
/**
 * What "Study Now" does for a single served question, which is not what it does
 * inside a set. The review form needs the set that holds the missed question to
 * mint a rateable deck from it; a question served on its own has none, so its
 * cards go in through the add-block route as a flip-through block instead. The
 * modal says which one the student is about to get.
 */
const SINGLE_QUESTION_STUDY_NOTE = 'Adds these cards to this chat.';

/**
 * What a refusal of a review set says. Most of these are a badge that has gone
 * stale — the student answered the question again, or the cards were unassigned
 * — so each says what changed rather than inviting a retry.
 */
const REVIEW_REFUSAL_MESSAGES: Record<
  StudySetReviewRefusal,
  string | undefined
> = {
  item_not_missed: 'This question is no longer marked as missed.',
  no_scheduled_variant: 'This question is no longer scheduled.',
  variant_mismatch: 'A different question is scheduled now.',
  no_linked_flashcards: 'Those flashcards are no longer assigned.',
  // Not stale: the student is mid-assessment and nothing may be served beside
  // it. The badge is worth keeping — it works again once they are done.
  live_attempt: 'Available after your current assessment.',
  content_unavailable: 'This question is no longer available.',
  rate_limited: 'Too many study sets for now. Try again shortly.',
  // The block's own generic line covers these: there is nothing to say
  // beyond "that did not work".
  not_found: undefined,
  unavailable: undefined,
};

/**
 * What the flashcards half says instead, where "this question" would be wrong.
 * The rest of the reasons read the same for both halves.
 */
const FLASHCARD_REFUSAL_MESSAGES: Partial<
  Record<StudySetReviewRefusal, string>
> = {
  // `no_linked_flashcards` keeps the base copy: "Study Now" is disabled at zero
  // enabled and never sends an empty list, so the only way to reach that refusal
  // is a server that finds nothing assigned — the switches in front of the
  // student are stale, and telling them to enable a card is a dead end.
  content_unavailable: 'These cards are no longer available.',
};

/**
 * Refusals that mean the content is gone rather than busy. The control that
 * asked withdraws itself, instead of inviting a tap that can only fail again.
 */
const REVIEW_CONTENT_GONE: ReadonlySet<StudySetReviewRefusal> = new Set([
  'content_unavailable',
]);

/**
 * Refusals that say the variant the badge was offering is not the one the server
 * schedules any more. What the student was previewing was the wrong question, so
 * the cached variant is dropped and read again rather than offered a second time.
 */
const REVIEW_STALE_VARIANT: ReadonlySet<StudySetReviewRefusal> = new Set([
  'no_scheduled_variant',
  'variant_mismatch',
]);

/** Which half of the badge row an add belongs to. */
type BadgeSlot = 'flashcards' | 'variant';

/**
 * What a review set is made of: the variant the preview is showing, or the cards
 * the curation modal left enabled (ORA-832). Both are checked server-side — the
 * variant against the student's scheduling, the ids against their assignments —
 * so naming them is a request, not an instruction.
 */
type ReviewSetContent =
  | { kind: 'question'; variantId: string }
  | { kind: 'flashcard'; flashcardIds: string[] };

/**
 * Where the question the badges sit under came from — which is what decides how
 * its linked content is asked for.
 */
export type QuestionStudyBadgeSource =
  /** A single served question: its own block names the content. */
  | { kind: 'question' }
  /** A question inside a study set: the set plus the question name it. */
  | { kind: 'study_set'; setId: string };

/** The badge props one question hands to `QuestionBlockBody`. */
export interface QuestionStudyBadgeProps {
  showStudyBadges: boolean;
  allowLinkedFlashcards: boolean;
  onAddLinkedFlashcardsBlock?: (
    flashcardIds: string[]
  ) => Promise<QuestionBadgeAddResult>;
  linkedFlashcardsStudyNote?: string;
  onAddScheduledVariantBlock?: (
    variantId: string
  ) => Promise<QuestionBadgeAddResult>;
  addBlocksUnavailableReason?: string;
}

export interface UseQuestionStudyBadgesArgs {
  /** The served block the question is drawn from; its ids carry the request. */
  block: ChatBlock;
  isReadOnly: boolean;
  source: QuestionStudyBadgeSource;
}

export interface QuestionStudyBadges {
  /**
   * The badge props for one question of this block.
   *
   * `isVisible` is false for a question that is mounted but not on screen — the
   * set's pager keeps every question mounted so an answered one stays answered
   * — and off means the badges are neither drawn nor fetched for.
   */
  badgePropsFor: (params: {
    vignetteId: string;
    isVisible?: boolean;
  }) => QuestionStudyBadgeProps;
}

export const useQuestionStudyBadges = ({
  block,
  isReadOnly,
  source,
}: UseQuestionStudyBadgesArgs): QuestionStudyBadges => {
  const dispatch = useDispatch<AppDispatch>();
  // Read at click time rather than subscribed to: nothing here renders from
  // the block map, and every question in the thread re-rendering whenever any
  // block lands is exactly what the thread is careful not to do.
  const store = useStore<ChatRootState>();
  const scrollToAddedBlock = useScrollToAddedBlock();
  /** What each badge added, by slot and question, so a repeat tap goes back. */
  const added = useRef(new Map<string, string>());

  // Read as defensively as the rest of the chat does: a narrow chat-only store
  // carries no exams slice, and the answer there is "nothing known" rather than
  // a crash.
  const primaryExam = useSelector((state: Partial<RootState>) =>
    state.exams
      ? examsSelectors.selectPrimaryExam(state as RootState)
      : undefined
  );

  /**
   * QBank's own two gates on the flashcard half of the row: a student whose exam
   * has flashcards switched off is never offered them (`LinkedFlashcards`), and
   * neither is a preclinical student on a question outside their daily session,
   * which every chat question is (`SRBadge`). Read exactly as QBank reads it —
   * an exam that says nothing is an exam without flashcards — so the two
   * surfaces never disagree about the same question.
   */
  const allowLinkedFlashcards =
    Boolean(primaryExam?.isUsingFlashcards) && !isPreclinicalExam(primaryExam);

  // One boolean, so the row re-renders when the turn names its answer and not
  // for anything else. It gates the served-question path alone: the review-set
  // route names the set and the question, not the answer they arrived in.
  const answerIsNamed = useSelector((state: ChatRootState) =>
    Boolean(state.chat.messages[block.messageId]?.serverId)
  );

  /** The block this badge already added, remembered or found in the thread. */
  const remember = (slot: BadgeSlot, vignetteId: string, blockId: string) => {
    added.current.set(`${slot}:${vignetteId}`, blockId);
  };

  const recall = (slot: BadgeSlot, vignetteId: string) =>
    added.current.get(`${slot}:${vignetteId}`) ?? null;

  /**
   * Study what the badge points at, here in the thread.
   *
   * A block this mount already added is scrolled to rather than asked for
   * again. Everything else goes to the server, which is idempotent: a tap
   * after a reload costs one request and comes back with the block the first
   * tap made, which is the id to scroll to either way.
   */
  const addReference = async (
    slot: BadgeSlot,
    vignetteId: string,
    reference: ChatAddBlockReference,
    findExisting?: () => string | null
  ): Promise<QuestionBadgeAddResult> => {
    const remembered = recall(slot, vignetteId) ?? findExisting?.() ?? null;

    if (remembered) {
      remember(slot, vignetteId, remembered);
      scrollToAddedBlock(remembered);
      return { ok: true, alreadyAdded: true };
    }

    const result = await dispatch(
      addReferenceBlock({
        messageId: block.messageId,
        blockId: block.id,
        reference,
      })
    );

    // A block-only message with no block in it is a malformed answer, not a
    // success: there is nothing to show and nothing to go back to.
    if (!result.ok || !result.blockId) return { ok: false };

    remember(slot, vignetteId, result.blockId);
    scrollToAddedBlock(result.blockId);
    return { ok: true };
  };

  /** The variant block already in the thread, if the badge has made one. */
  const findVariantBlock = (variantId: string) => {
    const chat = store.getState().chat;

    return findAddedVariantBlockId({
      blocks: Object.values(chat.blocks),
      visibleMessageIds:
        chat.conversations[block.conversationId]?.messageIds ?? [],
      variantId,
      sourceBlockId: block.id,
    });
  };

  /**
   * Study a missed question of a set, as a review set of its own (ORA-830).
   *
   * The same shape as `addReference` and for the same reason — a set this mount
   * already made is scrolled to — but the server is the one that resolves the
   * content, and it answers *why* when it will not: the reason becomes the line
   * the badge or the preview modal shows.
   */
  const addReviewSet = async (
    slot: BadgeSlot,
    fromSetId: string,
    vignetteId: string,
    content: ReviewSetContent
  ): Promise<QuestionBadgeAddResult> => {
    const remembered = recall(slot, vignetteId);

    if (remembered) {
      scrollToAddedBlock(remembered);
      return { ok: true, alreadyAdded: true };
    }

    const result = await dispatch(
      addReviewStudySetBlock({
        messageId: block.messageId,
        fromSetId,
        sourceVignetteId: vignetteId,
        ...content,
      })
    );

    if (!result.ok) {
      return {
        ok: false,
        message:
          (slot === 'flashcards'
            ? FLASHCARD_REFUSAL_MESSAGES[result.reason]
            : undefined) ?? REVIEW_REFUSAL_MESSAGES[result.reason],
        contentGone: REVIEW_CONTENT_GONE.has(result.reason),
        staleVariant: REVIEW_STALE_VARIANT.has(result.reason),
      };
    }

    remember(slot, vignetteId, result.blockId);
    scrollToAddedBlock(result.blockId);
    return { ok: true, alreadyAdded: result.alreadyAdded === true };
  };

  const badgePropsFor = ({
    vignetteId,
    isVisible = true,
  }: {
    vignetteId: string;
    isVisible?: boolean;
  }): QuestionStudyBadgeProps => {
    // A restored legacy question is an archive: its badges may not write
    // anything new, so they are not drawn at all.
    const showStudyBadges = !isReadOnly && isVisible;

    if (!showStudyBadges) {
      return { showStudyBadges: false, allowLinkedFlashcards };
    }

    if (source.kind === 'study_set') {
      const { setId } = source;

      return {
        showStudyBadges,
        allowLinkedFlashcards,
        onAddLinkedFlashcardsBlock: (flashcardIds: string[]) =>
          addReviewSet('flashcards', setId, vignetteId, {
            kind: 'flashcard',
            flashcardIds,
          }),
        onAddScheduledVariantBlock: (variantId: string) =>
          addReviewSet('variant', setId, vignetteId, {
            kind: 'question',
            variantId,
          }),
      };
    }

    // A served question whose answer the turn has not named yet cannot hang
    // anything off it, and says so rather than letting the badges fall back to
    // doing nothing in silence.
    if (!answerIsNamed) {
      return {
        showStudyBadges,
        allowLinkedFlashcards,
        addBlocksUnavailableReason: ANSWER_STILL_WRITING,
      };
    }

    return {
      showStudyBadges,
      allowLinkedFlashcards,
      linkedFlashcardsStudyNote: SINGLE_QUESTION_STUDY_NOTE,
      // The ids the modal curated are not sent here: the add-block route builds
      // the block from the student's own assignments, which is what the modal's
      // switches just wrote, so the two agree without naming anything.
      onAddLinkedFlashcardsBlock: () =>
        addReference('flashcards', vignetteId, { kind: 'linked_flashcards' }),
      onAddScheduledVariantBlock: (variantId: string) =>
        addReference(
          'variant',
          vignetteId,
          { kind: 'scheduled_variant', variantId },
          () => findVariantBlock(variantId)
        ),
    };
  };

  return { badgePropsFor };
};
